import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import SiteParcelsModal from './SiteParcelsModal';
import { plotSurfaceCheck } from './siteParcels';

// CBS calculé en ligne par l'API (cf. api/land_cover.py) : parcelle(s) du cadastre IGN +
// occupation du sol CoSIA. Le terrain se compose dans SiteParcelsModal ; sa validation
// enregistre tout de suite `cbs` et `cbs_detail`, sans passer par le bouton du formulaire.

const COSIA_WMS_URL = 'https://data.geopf.fr/wms-r/wms';
const EARTH_RADIUS = 6378137;
const MAP_WIDTH = 360;

const toMercator = ([lon, lat]) => [
  (EARTH_RADIUS * lon * Math.PI) / 180,
  EARTH_RADIUS * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)),
];

// Anneaux extérieurs d'un Polygon ou MultiPolygon GeoJSON, en Web Mercator.
const outerRings = (geometry) => {
  if (!geometry) return [];
  const polygons = geometry.type === 'MultiPolygon' ? geometry.coordinates : [geometry.coordinates];
  return polygons.map((polygon) => polygon[0].map(toMercator));
};

const formatNumber = (value, digits = 0) =>
  Number(value ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

function ParcelMap({ parcels, layer }) {
  const rings = parcels.flatMap((parcel) => outerRings(parcel.geometry));
  if (!rings.length || !layer) return null;
  const xs = rings.flat().map(([x]) => x);
  const ys = rings.flat().map(([, y]) => y);
  const margin = 0.15 * Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys), 50);
  const [minx, maxx, miny, maxy] = [Math.min(...xs) - margin, Math.max(...xs) + margin, Math.min(...ys) - margin, Math.max(...ys) + margin];
  const height = Math.round((MAP_WIDTH * (maxy - miny)) / (maxx - minx));
  const params = new URLSearchParams({
    SERVICE: 'WMS', VERSION: '1.3.0', REQUEST: 'GetMap', LAYERS: layer, STYLES: '', CRS: 'EPSG:3857',
    BBOX: [minx, miny, maxx, maxy].join(','), WIDTH: MAP_WIDTH * 2, HEIGHT: height * 2, FORMAT: 'image/png',
  });
  const points = (ring) => ring.map(([x, y]) => `${((x - minx) / (maxx - minx)) * MAP_WIDTH},${((maxy - y) / (maxy - miny)) * height}`).join(' ');
  return (
    <div className="cbs-panel-map" style={{ width: MAP_WIDTH, height }}>
      <img src={`${COSIA_WMS_URL}?${params}`} alt="Occupation du sol CoSIA autour de la parcelle" width={MAP_WIDTH} height={height} />
      <svg width={MAP_WIDTH} height={height}>
        {rings.map((ring, index) => <polygon key={index} points={points(ring)} />)}
      </svg>
    </div>
  );
}

export default function CbsPanel({ buildingId, latitude, longitude, detail, plotSurface, onComputed, onUsePlotSurface }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [computing, setComputing] = useState(false);

  if (!buildingId) {
    return <p className="cbs-panel-hint">Enregistrez le bâtiment pour calculer son CBS.</p>;
  }

  const parcels = detail?.parcels || [];
  const surfaceCheck = plotSurfaceCheck(detail?.surface_m2, plotSurface);

  // Enregistre le terrain (`cbs` et `cbs_detail`) ; renvoie vrai si c'est fait.
  const saveTerrain = async (parcelIds, successMessage) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/compute_cbs/${buildingId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parcel_ids: parcelIds }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(data.error || 'Erreur lors du calcul du CBS.');
        return false;
      }
      toast.success(successMessage(data));
      onComputed(data);
      return true;
    } catch (error) {
      toast.error('Calcul du CBS impossible : vérifiez la connexion.');
      return false;
    }
  };

  // Même terrain, millésime CoSIA le plus récent.
  const recompute = async () => {
    setComputing(true);
    await saveTerrain(parcels.map((parcel) => parcel.idu), (data) => `CBS recalculé : ${formatNumber(data.cbs, 2)}`);
    setComputing(false);
  };

  const applyPlotSurface = (value) => {
    onUsePlotSurface(value);
    toast.info(`Surface de parcelle mise à ${formatNumber(value)} m² : pensez à enregistrer le bâtiment.`);
  };

  return (
    <div className="cbs-panel">
      <p className="cbs-panel-hint">
        Terrain du client (une ou plusieurs parcelles du cadastre) et occupation du sol CoSIA (IGN), millésime le plus récent.
      </p>
      <div className="cbs-panel-actions">
        <button type="button" className="small-black-button" onClick={() => setModalOpen(true)}>
          {parcels.length ? 'Modifier le terrain' : 'Choisir le terrain et calculer le CBS'}
        </button>
        {parcels.length > 0 && (
          <button type="button" className="cbs-panel-secondary" onClick={recompute} disabled={computing}>
            {computing ? 'Calcul en cours…' : 'Recalculer'}
          </button>
        )}
        {parcels.length > 0 && (
          <Link className="cbs-panel-secondary" to={`/outils/cbs?building=${buildingId}`}>Comparer à des sites similaires</Link>
        )}
      </div>

      {detail && (
        <div className="cbs-panel-result">
          <div className="cbs-panel-figures">
            <p><strong>CBS :</strong> {formatNumber(detail.cbs, 2)}</p>
            <p><strong>CBS potentiel :</strong> {formatNumber(detail.cbs_potential, 2)} <span className="cbs-panel-hint">(zones imperméables désimperméabilisées)</span></p>
            <p>
              <strong>Terrain :</strong> {parcels.length} parcelle(s), {formatNumber(detail.surface_m2)} m² — surface éco-aménageable {formatNumber(detail.bbs_m2)} m²
            </p>
            <p className="cbs-panel-hint">{parcels.map((parcel) => parcel.idu).join(', ')}</p>
            {surfaceCheck.state === 'empty' && (
              <p className="cbs-panel-hint">
                Surface de parcelle non renseignée.{' '}
                <button type="button" className="site-parcels-link" onClick={() => applyPlotSurface(Math.round(detail.surface_m2))}>La remplir avec {formatNumber(detail.surface_m2)} m²</button>
              </p>
            )}
            {surfaceCheck.state === 'different' && (
              <p className="cbs-panel-warning">
                Surface de parcelle saisie : {formatNumber(surfaceCheck.entered)} m² ({surfaceCheck.gap > 0 ? '+' : ''}{Math.round(surfaceCheck.gap * 100)} % d’écart avec le terrain).{' '}
                <button type="button" className="site-parcels-link" onClick={() => applyPlotSurface(Math.round(detail.surface_m2))}>Remplacer par {formatNumber(detail.surface_m2)} m²</button>
              </p>
            )}
            <p className="cbs-panel-hint">
              {(detail.cosia_layers || []).join(', ').replace(/IGNF_COSIA_/g, 'CoSIA ')} — calculé le {new Date(detail.computed_at).toLocaleDateString('fr-FR')}
            </p>
            {(detail.warnings || []).map((warning) => <p key={warning} className="cbs-panel-warning">{warning}</p>)}
          </div>
          <ParcelMap parcels={parcels} layer={(detail.cosia_layers || [])[0]} />
          <table className="cbs-panel-table">
            <thead>
              <tr><th>Classe CoSIA</th><th>Classe CBS</th><th>Coeff.</th><th>Surface</th></tr>
            </thead>
            <tbody>
              {(detail.surfaces || []).map((row) => (
                <tr key={row.cosia_class}>
                  <td><span className="cbs-panel-swatch" style={{ background: row.color }} />{row.cosia_class}</td>
                  <td>{row.cbs_class}</td>
                  <td>{formatNumber(row.coefficient, 1)}</td>
                  <td>{formatNumber(row.surface_m2)} m²</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <SiteParcelsModal
          latitude={latitude}
          longitude={longitude}
          initialParcels={parcels}
          plotSurface={plotSurface}
          onClose={() => setModalOpen(false)}
          onValidate={(parcelIds) => saveTerrain(parcelIds, (data) => `Terrain enregistré : ${parcelIds.length} parcelle(s), CBS ${formatNumber(data.cbs, 2)}`)}
          onUsePlotSurface={applyPlotSurface}
        />
      )}
    </div>
  );
}
